// Identifying cylinders on a phone (Phase 3): by the QR label (its serial), a phone Reader over OTG (a tag's EPC) or
// the serial typed; the lookup of many at once and the sightings a camera sweep records. Aligned with the backend's
// src/modules/cylinders (GET /cylinders/lookup, POST /cylinders/sightings).
import { api, send } from './client';
import type { CylinderStatus, FillState, PositionStatus } from './common';
import { query } from './paging';


/** How the cylinder was identified (sent with every scan; an audit and a field-quality figure). */
export const ID_METHODS = ['QR', 'PHONE_READER', 'MANUAL'] as const;
export type IdMethod = (typeof ID_METHODS)[number];
/** One identification: the serial (a QR label, typed) or the tag's EPC (a phone Reader), and how it was taken. */
export type CylinderRef = { method: IdMethod; serial: string; epc?: never } | { method: IdMethod; epc: string; serial?: never };

/**
 * A body's fields for one cylinder as the backend takes them: `<prefix>Code` (the serial or the EPC: the server
 * resolves it through the binding in force) and `<prefix>Method` (`fullCode`, `fullMethod`; no prefix: `code`,
 * `method`); null = none (no empty handed back).
 */
export function refFields(prefix: string, ref: CylinderRef | null): Record<string, string | null> {
  const k = (s: string) => (prefix ? `${prefix}${s[0].toUpperCase()}${s.slice(1)}` : s);
  if (!ref) return { [k('code')]: null, [k('method')]: null };
  return { [k('code')]: ref.serial ?? ref.epc ?? null, [k('method')]: ref.method };
}

/** One answer of GET /cylinders/lookup, as the screens read it. */
export interface CylinderLookupHit {
  /** the code as asked */
  code: string;
  found: boolean;
  /** why not: INVALID (neither a serial nor an EPC), UNKNOWN, RETIRED (a tag no longer in force) */
  problem: 'INVALID' | 'UNKNOWN' | 'RETIRED' | null;
  id: number | null;
  serialNo: string | null;
  manufacturerSerial: string | null;
  status: CylinderStatus | null;
  positionStatus: PositionStatus | null;
  fillState: FillState | null;
  locationId: number | null;
  locationName: string | null;
}
/** cylinder-identify.service.ts › LookupItem */
export interface WireLookupItem { code: string; found: boolean; problem: CylinderLookupHit['problem']; cylinderId: number | null; serialNo: string | null; manufacturerSerial: string | null; status: CylinderStatus | null; positionStatus: PositionStatus | null; fillState: FillState | null; location: { id: number; name: string } | null }
/** GET /cylinders/lookup?codes=&method= (cylinder.read; at most 200 codes): what each code names, nothing recorded. */
export const lookupCylinders = (codes: string[], method: IdMethod) =>
  api<WireLookupItem[]>(`/cylinders/lookup${query({ codes: codes.join(','), method })}`).then((r) =>
    r.map(({ cylinderId, location, ...x }) => ({ ...x, id: cylinderId, locationId: location?.id ?? null, locationName: location?.name ?? null })),
  );

/** One find of a sweep: the code, how, a token of its own (a resend is recorded once), the device's time. */
export interface SightingItem { code: string; method: IdMethod; clientToken: string; clientAt: string }
/**
 * POST /cylinders/sightings {locationId | gps, items}: a find sweep's confirmations at one Titik Distribusi, or where
 * the phone is (a courier on the road: its position), recorded as sightings, never a movement. Per item: recorded.
 */
export const recordSightings = (where: { locationId: number } | { gps: { lat: number; lng: number; accuracyM: number } }, items: SightingItem[]) =>
  send<{ items: { code: string; recorded: boolean }[] }>('/cylinders/sightings', { ...where, items });
